import { Component, ComponentInterface, JSX, Prop, State, Watch, h } from '@stencil/core';
import { ProductCategory, SessionStorageKeys } from '@aglenergy/shared-library';
import { ProductCatalogueProduct, PromoDialogData } from 'app/shared/models/index';

import { StoreKeys } from 'app/shared/constants';
import { eligibilityCodeService } from 'app/shared/services/eligibility-code/eligibility-code.service';
import { productService } from 'app/shared/services/productService/product.service';
import { store } from 'app/shared/store/store';
import { tealiumAnalyticService } from 'app/shared/services';

@Component({
  tag: 'promo-dialog-dls',
  styleUrl: 'promo-dialog-dls.scss'
})
export class PromoDialogDls implements ComponentInterface {
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public promoDialogData: PromoDialogData;
  @Prop() public isOpen: boolean;
  @State() public eligibilityInputValid: boolean | null = null;
  @State() public firstNameInputValid: boolean | null = null;
  @State() public lastNameInputValid: boolean | null = null;
  @State() public eligibilityDataValid: boolean | null = null; // to indicate wether the input is empty or have invalid data to show correct error message
  @State() public firstNameDataValid: boolean | null = null; // to indicate wether the input is empty or have invalid data to show correct error message
  @State() public lastNameDataValid: boolean | null = null; // to indicate wether the input is empty or have invalid data to show correct error message
  @State() public eligibilityDataIsValid: boolean | null = null;
  @State() public isLoading: boolean;
  @State() public buttonClicked = false;
  @State() isMobile = false;
  @State() public firstName: string | null = null;
  @State() public lastName: string | null = null;
  @State() public eligibilityCode: string | null = null;
  @State() isGuidedFlow = false;

  private eligibilityCodeEl: HTMLAglDsTextboxElement;
  private firstNameCodeEl: HTMLAglDsTextboxElement;
  private lastNameCodeEl: HTMLAglDsTextboxElement;
  private termsCheckboxEl: HTMLAglDsCheckboxElement;
  private modalDialog: HTMLAglDsModalElement;
  @State() public partnerOfferDetails: string;

  @Watch('isOpen')
  watchIsOpenHandler(newValue: boolean): void {
    if (newValue) this.modalDialog.openModal(null);
  }

  disconnectedCallback(): void {
    window.removeEventListener('resize', this.updateIsMobile);
  }

  componentWillLoad(): Promise<void> | void {
    this.isMobile = this.isMobileDevice();
    window.addEventListener('resize', this.updateIsMobile);
    this.isGuidedFlow = store.get('isGuidedFlow');
  }

  private sendAnalyticsEvent(
    context: string,
    eventName: string,
    data: { [key: string]: string } = {},
    errorData?: {
      code?: string;
      description?: string;
      type?: string;
    }
  ) {
    const modalName = this.promoDialogData?.header;

    const updatedContext = [`[Modal][${context}][${modalName}]`];

    const updatedData = {
      ...data,
      modal_name: modalName,
      page_name: window['utag_data']?.page_name || 'unknown',
      page_URL: window['utag_data']?.page_URL || 'unknown'
    };

    const productCategory = store.get('productCategory');

    tealiumAnalyticService.sendEvent(updatedContext, null, eventName, updatedData, productCategory, errorData);
  }

  private handleModalOpen(): void {
    this.sendAnalyticsEvent('Displayed', 'modal_display');

    this.buttonClicked = false;
    this.eligibilityInputValid = null;
    this.eligibilityDataValid = null;
    this.eligibilityCodeEl.value = '';
    this.eligibilityCode = null;
    this.eligibilityDataIsValid = null;
    this.termsCheckboxEl.checked = false;

    if (this.promoDialogData?.showFirstName) {
      this.firstNameInputValid = null;
      this.firstNameCodeEl.value = '';
      this.firstName = null;
      this.firstNameDataValid = null;
    }
    if (this.promoDialogData?.showLastName) {
      this.lastNameCodeEl.value = '';
      this.lastName = null;
      this.lastNameInputValid = null;
      this.lastNameDataValid = null;
    }
  }

  private handleModalClose(): void {
    this.sendAnalyticsEvent('Modal', 'Closed', {}, {});
    store.set(StoreKeys.isPromoModalDlsOpen, false);
  }

  private validateRequiredTextBox(event: CustomEvent<FocusEvent> | CustomEvent<any>) {
    const target: HTMLAglDsTextboxElement = event.target as HTMLAglDsTextboxElement;
    if (target.id === this.eligibilityCodeEl.id) {
      this.eligibilityInputValid = target.value?.length > 0;
      this.eligibilityDataValid = null;
    } else if (target.id === this.firstNameCodeEl.id) {
      this.firstNameInputValid = target.value?.length > 0;
      this.firstNameDataValid = null;
    } else if (target.id === this.lastNameCodeEl.id) {
      this.lastNameInputValid = target.value?.length > 0;
      this.lastNameDataValid = null;
    }
  }

  private async validateAndSubmit(): Promise<void> {
    const firstNameValid = !this.promoDialogData?.showFirstName || this.firstNameInputValid === true;
    const lastNameValid = !this.promoDialogData?.showLastName || this.lastNameInputValid === true;
    this.buttonClicked = true;
    this.eligibilityDataIsValid = null;

    if (this.eligibilityInputValid && firstNameValid && lastNameValid && this.termsCheckboxEl?.checked) {
      await this.validateEligibilityCode();
      const productCategory = store.get('productCategory');
      if (this.eligibilityDataIsValid) {
        if (productCategory === ProductCategory.Nbn) {
          if (this.partnerOfferDetails) {
            window.sessionStorage.setItem(SessionStorageKeys.partnerOfferDetails, this.partnerOfferDetails);
          }
          productService.onSelectedProductForNbn(this.products, store.get('address'), store.get(StoreKeys.selectedSKU));
        } else if (productCategory?.toLowerCase() === ProductCategory.Mobile.toLowerCase()) {
          if (this.partnerOfferDetails) {
            window.sessionStorage.setItem(SessionStorageKeys.partnerOfferDetailsMobile, this.partnerOfferDetails);
          }
          productService.onSelectedProductForMobile(store.get(StoreKeys.selectedSKU), this.products, this.isGuidedFlow);
        }
      } else {
        this.eligibilityDataValid = this.eligibilityDataIsValid;
        this.firstNameDataValid = this.eligibilityDataIsValid;
        this.lastNameDataValid = this.eligibilityDataIsValid;
      }
    } else {
      if (this.eligibilityInputValid === null) this.eligibilityInputValid = false;
      if (this.promoDialogData?.showFirstName && this.firstNameInputValid === null) this.firstNameInputValid = false;
      if (this.promoDialogData?.showLastName && this.lastNameInputValid === null) this.lastNameInputValid = false;
    }
  }

  private async validateEligibilityCode(): Promise<void> {
    this.eligibilityCode = this.eligibilityCodeEl.value?.trim();
    this.firstName = this.promoDialogData?.showFirstName ? this.firstNameCodeEl.value?.trim() : null;
    this.lastName = this.promoDialogData?.showLastName ? this.lastNameCodeEl.value?.trim() : null;
    this.eligibilityDataValid = null;
    this.firstNameDataValid = null;
    this.lastNameDataValid = null;

    const productDetailsFromSession = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    const differentiatedPricing =
      productDetailsFromSession?.length > 0 ? productDetailsFromSession[0]?.response?.products[0]?.differentiatedPricing : null;
    const eligibilityValidationSource = differentiatedPricing ? differentiatedPricing?.eligibilityValidationSource : null;

    this.isLoading = true;

    await eligibilityCodeService
      .validateEligibilityCode(
        store.get(StoreKeys.webId),
        this.eligibilityCode?.replace(/\s+/g, ''),
        eligibilityValidationSource,
        this.firstName,
        this.lastName
      )
      .then((results) => {
        const resultIsValid = results?.isValid;
        this.eligibilityDataIsValid = resultIsValid === true;
        this.partnerOfferDetails = results?.dataToSet?.partnerOfferDetails;
        this.isLoading = false;
        return;
      });
  }

  private updateIsMobile = () => {
    this.isMobile = this.isMobileDevice();
  };

  private isMobileDevice() {
    return window.innerWidth < 768;
  }

  public render(): JSX.Element {
    return (
      <agl-ds-modal
        animationType="popup"
        disableScroll={true}
        ref={(el) => {
          if (el) this.modalDialog = el;
        }}
        onModalClosed={() => this.handleModalClose()}
        onModalOpened={() => this.handleModalOpen()}
      >
        <agl-ds-text slot="header" styledAs="xl" fontWeight="semibold" class="promo-dialog-header">
          {this.promoDialogData?.header}
        </agl-ds-text>
        <div slot="content">
          {this.buttonClicked && this.eligibilityDataIsValid === false && (
            <div class="error-container">
              <agl-ds-card appearance="border" class="error-card" corner="rounded">
                <div class="error-card-container">
                  <agl-ds-icon size={this.isMobile ? 'xs' : 'sm'} class="error-icon" iconPath="/apps/comparison/assets/icon/alert.svg" />
                  <agl-ds-trusted-html-content
                    styledAs="md"
                    containerBorder="none"
                    innerHTML={this.promoDialogData?.eligibilityCodeError}
                    containerSize="auto"
                    class="error-label"
                  />
                </div>
              </agl-ds-card>
            </div>
          )}
          <div class="dialog-form">
            <agl-ds-h4 id="eligibility-label" bottomMargin="space03">
              {this.promoDialogData?.eligibilityCodeLabel}
            </agl-ds-h4>
            <agl-ds-textbox
              id="eligibility-input-textbox"
              placeholder={this.promoDialogData?.eligibilityCodePlaceholder}
              label={this.promoDialogData?.eligibilityCodePlaceholder}
              validationText={
                this.eligibilityDataValid === false
                  ? this.promoDialogData?.eligibilityCodeHintText
                  : this.promoDialogData?.requiredFieldErrorMessage
              }
              hasError={this.eligibilityInputValid === false || this.eligibilityDataValid === false}
              hintText={this.promoDialogData?.eligibilityCodeHintText}
              onTextboxBlur={(e) => this.validateRequiredTextBox(e)}
              onValueChange={(e) => this.validateRequiredTextBox(e)}
              ref={(e) => {
                if (e) this.eligibilityCodeEl = e;
              }}
              isInputValid={this.eligibilityDataIsValid}
              value={this.eligibilityCode}
            />
            <div class="name-fields-container">
              {this.promoDialogData?.showFirstName && (
                <agl-ds-textbox
                  id="first-name-textbox"
                  placeholder="First Name"
                  label="First Name"
                  validationText={
                    this.firstNameDataValid === false
                      ? this.promoDialogData?.firstNameHintText
                      : this.promoDialogData?.requiredFieldErrorMessage
                  }
                  hintText={this.promoDialogData?.firstNameHintText}
                  hasError={this.firstNameInputValid === false || this.firstNameDataValid === false}
                  onTextboxBlur={(e) => this.validateRequiredTextBox(e)}
                  onValueChange={(e) => this.validateRequiredTextBox(e)}
                  ref={(e) => {
                    if (e) this.firstNameCodeEl = e;
                  }}
                  isInputValid={this.eligibilityDataIsValid}
                  value={this.firstName}
                />
              )}
              {this.promoDialogData?.showLastName && (
                <agl-ds-textbox
                  id="last-name-textbox"
                  placeholder="Last Name"
                  label="Last Name"
                  validationText={
                    this.lastNameDataValid === false
                      ? this.promoDialogData?.lastNameHintText
                      : this.promoDialogData?.requiredFieldErrorMessage
                  }
                  hintText={this.promoDialogData?.lastNameHintText}
                  hasError={this.lastNameInputValid === false || this.lastNameDataValid === false}
                  onTextboxBlur={(e) => this.validateRequiredTextBox(e)}
                  onValueChange={(e) => this.validateRequiredTextBox(e)}
                  ref={(e) => {
                    if (e) this.lastNameCodeEl = e;
                  }}
                  isInputValid={this.eligibilityDataIsValid}
                  value={this.lastName}
                />
              )}
            </div>
          </div>
          <agl-ds-trusted-html-content containerBorder="none" styledAs="xs" class="promo-dialog-content">
            {this.promoDialogData?.contentHeader?.length > 0 && (
              <agl-ds-h6 class="dialog-content-header" bottomMargin="space00">
                {this.promoDialogData?.contentHeader}
              </agl-ds-h6>
            )}
            {this.promoDialogData?.content?.length > 0 && (
              <agl-ds-trusted-html-content containerBorder="none" containerSize="auto" innerHTML={this.promoDialogData?.content} />
            )}
          </agl-ds-trusted-html-content>
          <div class="terms-container">
            {this.promoDialogData?.termsHeader && (
              <agl-ds-text class="terms-content-header">{this.promoDialogData?.termsHeader}</agl-ds-text>
            )}
            {this.promoDialogData?.termsContent && (
              <agl-ds-trusted-html-content
                containerBorder="none"
                containerSize="auto"
                innerHTML={this.promoDialogData?.termsContent}
                styledAs="sm"
              />
            )}
            {this.promoDialogData?.checkboxLabel && (
              <agl-ds-checkbox id="terms-checkbox" ref={(e) => (this.termsCheckboxEl = e)}>
                {this.promoDialogData?.checkboxLabel}
              </agl-ds-checkbox>
            )}
            {this.promoDialogData?.termsError && this.buttonClicked && !this.termsCheckboxEl?.checked && (
              <agl-ds-trusted-html-content
                styledAs="xs"
                containerBorder="none"
                innerHTML={this.promoDialogData?.termsError}
                containerSize="auto"
                class="terms-error-label"
              />
            )}
          </div>
          <div class="dialog-footer">
            <agl-ds-button id="promo-validate-button" onClick={() => this.validateAndSubmit()} loading={this.isLoading}>
              {this.promoDialogData?.submitButtonLabel}
            </agl-ds-button>
          </div>
        </div>
      </agl-ds-modal>
    );
  }
}
